import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Scale, PiggyBank, ArrowRight } from "lucide-react";

const DS_ROUTE = "/2-percent-direct-sale";
const fade = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-80px" }, transition: { duration: 0.6 } };

const CARDS = [
  { icon: Scale, accent: "text-brand-emerald", kicker: "1% + 1% = 2% total", title: "Transparent fee structure", body: "A 1% listing fee for full-service marketing and advocacy, plus 1% for the transaction coordination and escrow oversight an unrepresented buyer lacks." },
  { icon: ShieldCheck, accent: "text-brand-ink", kicker: "Zero dual agency", title: "Dedicated seller loyalty", body: "Equitas represents you exclusively. The buyer stays unrepresented, so our negotiation strategy and confidential advice belong entirely to you." },
  { icon: PiggyBank, accent: "text-brand-gold", kicker: "Illustrative: 2% vs 5%", title: "Real equity preservation", body: "On a $2,000,000 San Jose home, 2% total is $40,000 — an illustrative $60,000 less than a 5% model. Commissions are negotiable and not set by law." },
];

export default function DirectSaleHomeFeature() {
  return (
    <section className="bg-brand-sand/60 border-y border-brand-hairline" data-testid="home-direct-sale-feature">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-24">
        <motion.div {...fade} className="max-w-3xl">
          <span className="eyebrow">The Direct Sale Advantage</span>
          <h2 className="mt-3 font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-brand-ink leading-[1.1]">
            When an unrepresented buyer makes an offer,<br className="hidden sm:block" /> <span className="text-brand-emerald">your total commission is just 2%.</span>
          </h2>
          <p className="mt-5 text-brand-stone leading-relaxed">
            When a buyer discovers your home without an agent, you shouldn't be locked into high buyer-broker fees. With the Equitas Homes 2% Direct Sale your total brokerage commission is 2% of the gross sale price — while you keep exclusive, single-agency seller representation.
          </p>
        </motion.div>

        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {CARDS.map((c, i) => (
            <motion.div key={c.title} {...fade} transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-2xl bg-white border border-brand-hairline p-6 shadow-[0_4px_20px_rgba(27,59,43,0.04)]" data-testid="home-ds-card">
              <c.icon className={`h-6 w-6 ${c.accent}`} />
              <p className={`mt-4 font-serif text-xl ${c.accent}`}>{c.kicker}</p>
              <h3 className="mt-1 font-semibold text-brand-ink">{c.title}</h3>
              <p className="mt-2 text-sm text-brand-stone leading-relaxed">{c.body}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-4">
          <Link to="/" data-testid="home-ds-calc" className="inline-flex items-center gap-2 rounded-xl bg-brand-emerald px-6 py-3 text-sm font-semibold text-brand-cream hover:opacity-90 transition-opacity">Calculate your net proceeds <ArrowRight className="h-4 w-4" /></Link>
          <Link to={DS_ROUTE} data-testid="home-ds-learn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-emerald hover:underline">Learn how the 2% Direct Sale works <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <p className="mt-4 text-xs text-brand-stone/60">Commissions are negotiable. Broker: Louis Fong · CA DRE #01151598 · Serving San Jose, Cupertino, Sunnyvale, and across Silicon Valley.</p>
      </div>
    </section>
  );
}
